<template>
  <div class="row-root" :class="{ disabled }">
    <div class="row-main">
      <div class="icon-well" aria-hidden="true">{{ npcIcon }}</div>
      <div class="content">
        <div class="topline">
          <div class="name" :title="npc.name">{{ npc.name }}</div>
          <UiChip :variant="dispositionVariant">{{ npc.disposition }}</UiChip>
        </div>
        <div class="subline text-muted">
          <span>{{ npc.role }}</span>
        </div>
      </div>
    </div>

    <div class="row-actions">
      <div class="row-actions__interact">
        <UiIconButton
          variant="ghost"
          size="sm"
          :disabled="disabled"
          :title="`Inspect ${npc.name}`"
          @click="emit('inspect', npc)"
        >
          {{ ICONS.actions.inspect }}
        </UiIconButton>

        <UiIconButton
          v-if="npc.disposition !== 'hostile'"
          variant="ghost"
          size="sm"
          :disabled="disabled"
          :title="`Talk to ${npc.name}`"
          @click="emit('talk', npc)"
        >
          {{ ICONS.actions.talk }}
        </UiIconButton>

        <UiIconButton
          v-if="npc.disposition !== 'hostile'"
          variant="ghost"
          size="sm"
          :disabled="disabled"
          :title="`Steal from ${npc.name}`"
          @click="emit('steal', npc)"
        >
          {{ ICONS.actions.steal }}
        </UiIconButton>

        <UiIconButton
          v-if="npc.disposition !== 'hostile'"
          variant="ghost"
          size="sm"
          :disabled="disabled"
          :title="`Influence ${npc.name}`"
          @click="emit('influence', npc)"
        >
          {{ ICONS.actions.influence }}
        </UiIconButton>
      </div>

      <div class="row-actions__primary">
        <UiButton
          size="sm"
          tone="combat"
          :disabled="disabled"
          :title="disabled ? 'Combat unavailable right now' : `Engage ${npc.name}`"
          @click="emit('engage', npc.id)"
        >
          <template #icon>{{ ICONS.domains.combat }}</template>
          Combat
        </UiButton>
        <UiButton
          v-if="npc.hasShop"
          size="sm"
          tone="shop"
          :disabled="disabled"
          :title="`Shop with ${npc.name}`"
          @click="emit('trade', npc)"
        >
          <template #icon>{{ ICONS.actions.trade }}</template>
          Shop
        </UiButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { Npc } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import UiButton from "../../ui/UiButton.vue";
import UiChip from "../../ui/UiChip.vue";
import UiIconButton from "../../ui/UiIconButton.vue";

const props = withDefaults(
  defineProps<{
    npc: Npc;
    disabled?: boolean;
  }>(),
  {
    disabled: false
  }
);

const emit = defineEmits<{
  engage: [npcId: string];
  trade: [npc: Npc];
  talk: [npc: Npc];
  influence: [npc: Npc];
  inspect: [npc: Npc];
  steal: [npc: Npc];
}>();

const dispositionVariant = computed(() => {
  if (props.npc.disposition === "hostile") return "danger";
  if (props.npc.disposition === "friendly") return "active";
  return "idle";
});

const npcIcon = computed(() => {
  if (props.npc.disposition === "hostile") return ICONS.domains.combat;
  if (props.npc.hasShop) return ICONS.actions.trade;
  switch (props.npc.role) {
    case "guard":
      return ICONS.actions.combat;
    case "trainer":
      return ICONS.sessions.skills;
    case "quest-giver":
      return ICONS.actions.dice;
    case "vendor":
      return ICONS.actions.trade;
    case "wanderer":
      return ICONS.actions.travel;
    default:
      return ICONS.inspectKinds.npc;
  }
});
</script>

<style scoped>
.row-root {
  display: grid;
  gap: 0.75rem;
  padding: 0.75rem 0.8rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
}

.row-root.disabled {
  opacity: 0.72;
  pointer-events: none;
}

.row-main {
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
  min-width: 0;
}

.icon-well {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
  flex: 0 0 auto;
}

.content {
  min-width: 0;
  display: grid;
  gap: 0.25rem;
}

.topline {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
}

.name {
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: min(320px, 46vw);
}

.subline {
  font-size: 0.9rem;
}

.row-actions {
  display: grid;
  gap: 0.45rem;
  justify-items: end;
}

.row-actions__interact {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.row-actions__primary {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
